import React, { useEffect, useState } from "react";
import { useParams, useSearchParams } from "react-router-dom";
import { api } from "@/lib/api";
import { COMPANY_NAME, COMPANY_SHORT, COMPANY_INITIALS } from "@/lib/brand";
import { Printer } from "lucide-react";

export default function OrderPrint() {
  const { id } = useParams();
  const [params] = useSearchParams();
  const rawType = params.get("type") || "dispatch";
  // Backward compat: legacy 'order_note' === 'production_note'
  const docType =
    rawType === "order_note" ? "production_note" : rawType; // 'dispatch' | 'production_note' | 'client_note'

  const [order, setOrder] = useState(null);
  const [customer, setCustomer] = useState(null);
  const [products, setProducts] = useState([]);
  const [rawMaterials, setRawMaterials] = useState([]);
  const [assigned, setAssigned] = useState(null);
  const [company, setCompany] = useState(null);
  const [clientCompact, setClientCompact] = useState(false);

  useEffect(() => {
    async function load() {
      const [orders, custs, prods, rms, asg, comp] = await Promise.all([
        api.get("/orders"),
        api.get("/customers"),
        api.get("/products"),
        api.get("/raw-materials"),
        api.get(`/orders/${id}/assigned`),
        api.get("/company/profile"),
      ]);
      const o = orders.data.find((x) => x.id === id);
      setOrder(o);
      if (o) setCustomer(custs.data.find((c) => c.id === o.customer_id));
      setProducts(prods.data);
      setRawMaterials(rms.data);
      setAssigned(asg.data);
      setCompany(comp.data);
    }
    load();
  }, [id]);

  if (!order || !assigned) {
    return <div className="p-8 font-mono text-xs text-zinc-500">LOADING...</div>;
  }

  // Decide rendering mode: assigned-cartons (actual weights) vs spec-estimate (pre-assignment)
  const hasAssigned = (assigned.cartons || []).length > 0;
  const isProductionNote = docType === "production_note";
  const isClientNote = docType === "client_note";
  const isOrderNote = isProductionNote || isClientNote; // doc is one of the two notes (not DC)
  const isDispatchedOrder = order.status === "dispatched" || order.status === "delivered";

  // Hard guard: Delivery Challan is only printable once stock is assigned OR the order is already dispatched.
  if (!hasAssigned && !isDispatchedOrder && docType === "dispatch") {
    return (
      <div className="min-h-screen bg-zinc-100 flex items-center justify-center p-8" data-testid="print-locked">
        <div className="max-w-md w-full bg-white border border-zinc-300 p-8 text-center">
          <div className="text-[10px] font-mono uppercase tracking-widest text-zinc-500 mb-2">Document Locked</div>
          <h1 className="text-2xl font-bold text-zinc-950 mb-2">Delivery Challan not yet available</h1>
          <p className="text-sm text-zinc-700 mb-6">
            Order <span className="font-mono text-orange-700 font-semibold">{order.order_no}</span> has no stock assigned yet. Net / Tare / Gross weights are computed from the actual cartons attached to the order — assign stock first to enable this document.
          </p>
          <div className="flex flex-col gap-2">
            <a href={`/orders/${id}/assign`} className="text-[11px] uppercase tracking-wider font-mono px-4 py-2 bg-orange-600 hover:bg-orange-700 text-white inline-block">→ Assign Stock to {order.order_no}</a>
            <a href={`/orders/${id}/print?type=production_note`} className="text-[11px] uppercase tracking-wider font-mono px-4 py-2 border border-zinc-300 text-zinc-700 hover:bg-zinc-100 inline-block">Print Production Note instead</a>
            <a href="/orders" className="text-[10px] font-mono text-zinc-500 hover:text-orange-700 mt-2">← Back to Orders</a>
          </div>
        </div>
      </div>
    );
  }

  const productById = {};
  products.forEach((p) => { productById[p.id] = p; });

  // Group assigned cartons by product for nicer presentation
  const assignedByPid = {};
  (assigned.cartons || []).forEach((c) => {
    if (!assignedByPid[c.product_id]) assignedByPid[c.product_id] = [];
    assignedByPid[c.product_id].push(c);
  });

  // Compute order-level totals
  const totalCartons = hasAssigned ? assigned.totals.cartons : order.items.reduce((s, it) => s + (it.quantity_rolls || 0), 0);
  const totalNetKg = hasAssigned ? assigned.totals.net_kg : null;     // estimates filled later
  const totalGrossKg = hasAssigned ? assigned.totals.gross_kg : null;
  const totalTareKg = hasAssigned ? assigned.totals.tare_kg : null;

  const title = isClientNote
    ? "ORDER ACKNOWLEDGMENT"
    : isProductionNote
      ? "PRODUCTION NOTE — INTERNAL"
      : "DISPATCH CHALLAN";

  // Invoice/Amount UI removed — accounts module not integrated yet.

  return (
    <div className="min-h-screen bg-zinc-200 print:bg-white">
      {/* Toolbar (screen only) */}
      <div className="print:hidden bg-zinc-900 text-white px-6 py-3 flex items-center justify-between sticky top-0 z-50">
        <div className="font-mono text-xs uppercase tracking-widest">{title} · {order.order_no}{(!hasAssigned && !isOrderNote) && " (Estimate — stock not yet assigned)"}</div>
        <div className="flex gap-2">
          <a href={`/orders/${id}/print?type=production_note`} className={`text-[11px] uppercase font-mono px-3 py-1.5 border ${isProductionNote ? "bg-orange-600 border-orange-600" : "border-zinc-700 hover:bg-zinc-800"}`}>Production Note</a>
          <a href={`/orders/${id}/print?type=client_note`} className={`text-[11px] uppercase font-mono px-3 py-1.5 border ${isClientNote ? "bg-orange-600 border-orange-600" : "border-zinc-700 hover:bg-zinc-800"}`}>Client Note</a>
          {(hasAssigned || isDispatchedOrder) && (
            <a href={`/orders/${id}/print?type=dispatch`} className={`text-[11px] uppercase font-mono px-3 py-1.5 border ${docType === "dispatch" ? "bg-orange-600 border-orange-600" : "border-zinc-700 hover:bg-zinc-800"}`}>Delivery Challan</a>
          )}
          {isClientNote && (
            <button
              data-testid="client-compact-toggle"
              onClick={() => setClientCompact((v) => !v)}
              className={`text-[11px] uppercase font-mono px-3 py-1.5 border ${clientCompact ? "bg-zinc-800 border-zinc-700" : "border-zinc-700 hover:bg-zinc-800"}`}
              title="Toggle compact font size for the Order Note (default is large +4pt)"
            >{clientCompact ? "Large font" : "Compact"}</button>
          )}
          <button onClick={() => window.print()} data-testid="print-btn" className="ml-2 text-[11px] uppercase font-mono px-4 py-1.5 bg-orange-600 hover:bg-orange-700 flex items-center gap-2"><Printer size={12} /> Print / Save PDF</button>
        </div>
      </div>

      {/* A4 sheet — horizontally scrollable on small screens; locked to A4 for print */}
      <div className="overflow-x-auto print:overflow-visible">
      <div className="max-w-[210mm] w-full mx-auto my-3 sm:my-6 print:my-0 bg-white border border-zinc-300 print:border-0 shadow print:shadow-none relative" data-testid="doc-sheet">
        {/* Diagonal watermark on print */}
        <div className="absolute inset-0 flex items-center justify-center pointer-events-none select-none print:opacity-100" aria-hidden>
          <span
            style={{
              fontFamily: "IBM Plex Mono, monospace",
              fontSize: "120px",
              fontWeight: 700,
              color: "rgba(234,88,12,0.07)",
              transform: "rotate(-28deg)",
              letterSpacing: "0.15em",
              whiteSpace: "nowrap",
              textTransform: "uppercase",
            }}
          >{COMPANY_SHORT}</span>
        </div>

        <div className="p-4 sm:p-6 md:p-10 print:p-10 relative">
          {/* Header */}
          <div className="flex items-start justify-between border-b-2 border-zinc-900 pb-4">
            <div className="flex items-center gap-3">
              <div className="h-12 w-12 bg-orange-600 flex items-center justify-center text-white font-mono font-bold">{COMPANY_INITIALS}</div>
              <div>
                <div className="text-xl font-bold tracking-tight text-zinc-950">{company?.name || COMPANY_NAME}</div>
                <div className="text-[10px] font-mono uppercase tracking-widest text-zinc-600">{company?.tagline || "Stretch Film Manufacturing"}</div>
                <div className="text-[10px] text-zinc-600 mt-1">
                  GSTIN: {company?.gstin || "—"} · Email: {company?.email || "—"} · Phone: {company?.phone || "—"}
                </div>
                {company?.address && <div className="text-[10px] text-zinc-600 mt-0.5 whitespace-pre-line max-w-md">{company.address}</div>}
              </div>
            </div>
            <div className="text-right">
              <div className="text-[10px] font-mono uppercase tracking-widest text-zinc-500">{title}</div>
              <div className="text-2xl font-bold tracking-tight text-zinc-950 font-mono">{order.order_no}</div>
              <div className="text-[10px] font-mono text-zinc-700 mt-1">Order Date: {order.order_date}</div>
              <div className="text-[10px] font-mono text-zinc-700">Delivery: <span className="text-orange-700 font-semibold">{order.delivery_date}</span></div>
              <div className="mt-2 text-[10px] uppercase border px-2 py-0.5 inline-block font-mono tracking-widest border-orange-300 bg-orange-50 text-orange-800">{order.status.replace("_", " ")}</div>
            </div>
          </div>

          {/* Customer */}
          <div className="grid grid-cols-2 gap-6 mt-6">
            <div>
              <div className="text-[10px] font-mono uppercase tracking-widest text-zinc-500 mb-1">Bill To</div>
              <div className="border border-zinc-300 p-3">
                <div className="font-semibold text-zinc-950">{customer?.company || customer?.name || "—"}</div>
                <div className="text-xs text-zinc-700">{customer?.name}</div>
                <div className="text-xs text-zinc-700 whitespace-pre-line">{customer?.address}</div>
                <div className="text-[10px] font-mono text-zinc-600 mt-2">
                  GST: {customer?.gst || "—"} · {customer?.phone || ""} · {customer?.email || ""}
                </div>
              </div>
            </div>
            <div>
              <div className="text-[10px] font-mono uppercase tracking-widest text-zinc-500 mb-1">Document Details</div>
              <div className="border border-zinc-300 p-3 text-xs font-mono">
                <div className="flex justify-between"><span className="text-zinc-600">Document No:</span><span className="text-zinc-950">{order.order_no}</span></div>
                <div className="flex justify-between"><span className="text-zinc-600">Issued:</span><span className="text-zinc-950">{new Date().toLocaleDateString("en-IN")}</span></div>
                {!isClientNote && !isProductionNote && (
                  <>
                    <div className="flex justify-between"><span className="text-zinc-600">Vehicle No:</span><span className="text-zinc-950">________________</span></div>
                    <div className="flex justify-between"><span className="text-zinc-600">Transporter:</span><span className="text-zinc-950">________________</span></div>
                    <div className="flex justify-between"><span className="text-zinc-600">E-Way Bill No:</span><span className="text-zinc-950">________________</span></div>
                  </>
                )}
              </div>
            </div>
          </div>

          {!hasAssigned && !isOrderNote && (
            <div className="mt-6 border border-dashed border-orange-400 bg-orange-50 text-xs text-orange-800 p-3 font-mono">
              ⚠ Stock has not been assigned to this order yet. Weights below are estimates from the SKU spec. Open <span className="font-semibold">Assign Stock</span> from the Orders list to attach cartons and generate the final challan with actual Net / Tare / Gross.
            </div>
          )}

          {isProductionNote && (
            <div className="mt-6 border border-dashed border-zinc-400 bg-zinc-50 text-xs text-zinc-700 p-3 font-mono">
              INTERNAL PRODUCTION NOTE — for shop-floor planning. Raw material below excludes cartons already assigned from finished stock ({assigned?.totals?.cartons || 0} of {(order.items || []).reduce((s, it) => s + (it.quantity_rolls || 0), 0)} cartons already in stock). Production team only needs to produce the remaining cartons.
            </div>
          )}

          {isClientNote && (
            <div className="mt-6 border border-dashed border-orange-400 bg-orange-50 text-xs text-orange-800 p-3 font-mono">
              ORDER ACKNOWLEDGMENT — for the customer. Confirms what has been booked. Final dispatch weights will be on the Delivery Challan.
            </div>
          )}

          {/* Body */}
          {isClientNote ? (
            <ClientNoteTable order={order} productById={productById} compact={clientCompact} />
          ) : isProductionNote ? (
            <>
              <ProductionItemsTable order={order} productById={productById} assignedByPid={assignedByPid} rawMaterials={rawMaterials} />
              {/* Itemised RM moves to its own page on print */}
              <div className="break-before-page print:break-before-page">
                <RawMaterialBreakdown order={order} productById={productById} rawMaterials={rawMaterials} assignedByPid={assignedByPid} />
              </div>
            </>
          ) : hasAssigned ? (
            <AssignedItemsTable
              order={order}
              productById={productById}
              assignedByPid={assignedByPid}
              rawMaterials={rawMaterials}
            />
          ) : (
            <EstimatedItemsTable order={order} productById={productById} />
          )}

          {/* Totals strip moved INTO AssignedItemsTable so it can adapt to weight_category */}

          {/* Amount panel removed — no accounts module */}

          {order.notes && (
            <div className="mt-4 border border-dashed border-zinc-300 p-3 text-xs">
              <div className="text-[10px] font-mono uppercase tracking-widest text-zinc-500 mb-1">Notes</div>
              <div className="text-zinc-700">{order.notes}</div>
            </div>
          )}

          {/* Footer / signatures */}
          <div className={`mt-12 grid gap-6 text-xs ${isOrderNote ? "grid-cols-2" : "grid-cols-3"}`}>
            <div>
              <div className="border-t border-zinc-400 pt-2 text-[10px] font-mono uppercase tracking-widest text-zinc-600">Prepared by</div>
            </div>
            {!isOrderNote && (
              <div>
                <div className="border-t border-zinc-400 pt-2 text-[10px] font-mono uppercase tracking-widest text-zinc-600">Driver / Transporter</div>
              </div>
            )}
            <div>
              <div className="border-t border-zinc-400 pt-2 text-[10px] font-mono uppercase tracking-widest text-zinc-600">Received by (Customer)</div>
            </div>
          </div>

          <div className="mt-8 text-center text-[9px] font-mono uppercase tracking-widest text-zinc-500">
            {COMPANY_NAME} · Computer-generated document · {isOrderNote ? "Internal use only" : "Goods once dispatched will not be taken back without prior agreement"}
          </div>
        </div>
      </div>
      </div>

      <style>{`
        @media print {
          @page { size: A4; margin: 10mm; }
          body { background: white !important; }
        }
      `}</style>
    </div>
  );
}

/* === Helpers === */

function AssignedItemsTable({ order, productById, assignedByPid, rawMaterials }) {
  // Per-item grouping (restored Iter-22): one sub-table per SKU; rows are cartons.
  // Columns vary with weight_category. Item header carries the SKU + spec + assigned count.
  // +2pt fonts and wider spacing retained from Iter-20.
  const rmById = {};
  (rawMaterials || []).forEach((rm) => { rmById[rm.id] = rm; });

  const lineGroups = order.items.filter((it) => it.product_id).map((it, lineIdx) => {
    const p = productById[it.product_id];
    const spec = { ...(p?.spec || {}), ...(it.spec || {}) };
    const cartons = assignedByPid[it.product_id] || [];
    const cat = it.weight_category || "pure_net";
    const coreItem = p?.core_item_id ? rmById[p.core_item_id] : null;
    const cartonItem = p?.carton_item_id ? rmById[p.carton_item_id] : null;
    const corePerCarton = (coreItem?.unit_weight_kg || 0) * (spec.rolls_per_carton || 1);
    const cartonPerCarton = cartonItem?.unit_weight_kg || 0;
    const customTare = parseFloat(it.custom_tare_kg) || 0;

    const sizeBits = [];
    if (spec.width_mm) sizeBits.push(`${spec.width_mm}mm`);
    if (spec.thickness_micron) sizeBits.push(`${spec.thickness_micron}µ`);
    if (spec.roll_length_m) sizeBits.push(`${spec.roll_length_m}m`);

    let lineGross = 0, lineReported = 0;
    const rows = cartons.map((c, idx) => {
      const g = c.gross_weight_kg || 0;
      let reported;
      if (cat === "gross") reported = g;
      else if (cat === "net") reported = Math.max(0, g - corePerCarton);
      else if (cat === "custom") reported = Math.max(0, g - customTare);
      else reported = Math.max(0, g - corePerCarton - cartonPerCarton); // pure_net
      lineGross += g;
      lineReported += reported;
      return { id: c.id, serial: idx + 1, box_no: c.box_no, prod_date: c.production_date, gross: g, reported };
    });
    return { lineIdx, sku: p?.sku || "—", spec, sizeBits, required: it.quantity_rolls, assignedN: cartons.length, cat, rows, lineGross, lineReported, corePerCarton, cartonPerCarton, customTare };
  });

  const grandGross = lineGroups.reduce((s, lg) => s + lg.lineGross, 0);
  const grandReported = lineGroups.reduce((s, lg) => s + lg.lineReported, 0);
  const totalCartons = lineGroups.reduce((s, lg) => s + lg.rows.length, 0);

  const reportedLabel = (cat) => cat === "gross" ? "Weight per Carton" : cat === "net" ? "Weight per Carton" : cat === "custom" ? "Net (custom tare)" : "Net Weight";

  return (
    <div className="mt-6 space-y-5" data-testid="items-table">
      {lineGroups.map((lg) => {
        const showGrossCol = lg.cat !== "gross";
        const showCoreCol = lg.cat === "net";
        const showTareCol = lg.cat === "pure_net" || lg.cat === "custom";
        const tareValueLabel = lg.cat === "pure_net" ? "Tare Weight (kg)" : "Tare Weight (kg)";
        return (
          <div key={lg.lineIdx} className="border border-zinc-300" data-testid={`item-${lg.lineIdx}`}>
            <div className="bg-zinc-50 border-b border-zinc-900 px-3 py-2.5 font-mono text-[11px] uppercase tracking-wider text-zinc-700">
              <div>{lg.lineIdx + 1}. <span className="font-semibold text-zinc-950">{lg.sku}</span>{lg.sizeBits.length ? ` · ${lg.sizeBits.join(" · ")}` : ""} · Required {lg.required} ctns · Assigned {lg.assignedN} ctns</div>
            </div>
            <div className="overflow-x-auto print:overflow-visible">
            <table className="w-full text-sm border-collapse">
              <thead className="bg-zinc-100">
                <tr className="font-mono uppercase tracking-wider text-[11px] text-zinc-700">
                  <th className="text-left px-3 py-2 border-b border-zinc-300 w-10">#</th>
                  <th className="text-left px-3 py-2 border-b border-zinc-300">Box No</th>
                  <th className="text-left px-3 py-2 border-b border-zinc-300">Production Date</th>
                  {showGrossCol && <th className="text-right px-3 py-2 border-b border-zinc-300">Gross Weight (kg)</th>}
                  {showCoreCol && <th className="text-right px-3 py-2 border-b border-zinc-300">Tare Weight (kg)</th>}
                  {showTareCol && <th className="text-right px-3 py-2 border-b border-zinc-300">{tareValueLabel}</th>}
                  <th className="text-right px-3 py-2 border-b border-zinc-300 bg-orange-50">{reportedLabel(lg.cat)} (kg)</th>
                </tr>
              </thead>
              <tbody className="font-mono">
                {lg.rows.length === 0 ? (
                  <tr><td colSpan={6} className="text-center text-zinc-500 py-4 font-sans">No cartons assigned for this item.</td></tr>
                ) : lg.rows.map((r) => (
                  <tr key={r.id} className="border-b border-zinc-200">
                    <td className="px-3 py-2 border border-zinc-300 align-top">{r.serial}</td>
                    <td className="px-3 py-2 border border-zinc-300 align-top font-semibold">{r.box_no}</td>
                    <td className="px-3 py-2 border border-zinc-300 align-top">{r.prod_date}</td>
                    {showGrossCol && <td className="px-3 py-2 border border-zinc-300 text-right align-top">{r.gross.toFixed(3)}</td>}
                    {showCoreCol && <td className="px-3 py-2 border border-zinc-300 text-right align-top text-zinc-600">{lg.corePerCarton.toFixed(3)}</td>}
                    {showTareCol && <td className="px-3 py-2 border border-zinc-300 text-right align-top text-zinc-600">{(r.gross - r.reported).toFixed(3)}</td>}
                    <td className="px-3 py-2 border border-zinc-300 text-right align-top text-orange-700 font-semibold bg-orange-50">{r.reported.toFixed(3)}</td>
                  </tr>
                ))}
              </tbody>
              {lg.rows.length > 0 && (
                <tfoot className="font-mono">
                  <tr className="bg-zinc-50 border-t border-zinc-300">
                    <td className="px-3 py-2 border border-zinc-300 uppercase text-[11px] text-zinc-600" colSpan={3}>Sub-total · {lg.rows.length} ctns</td>
                    {showGrossCol && <td className="px-3 py-2 border border-zinc-300 text-right">{lg.lineGross.toFixed(3)}</td>}
                    {showCoreCol && <td className="px-3 py-2 border border-zinc-300 text-right text-zinc-600">{(lg.corePerCarton * lg.rows.length).toFixed(3)}</td>}
                    {showTareCol && <td className="px-3 py-2 border border-zinc-300 text-right text-zinc-600">{(lg.lineGross - lg.lineReported).toFixed(3)}</td>}
                    <td className="px-3 py-2 border border-zinc-300 text-right text-orange-700 bg-orange-50">{lg.lineReported.toFixed(3)}</td>
                  </tr>
                </tfoot>
              )}
            </table>
            </div>
          </div>
        );
      })}
      <div className="border-2 border-zinc-900 bg-zinc-50 px-3 py-2 grid grid-cols-3 gap-3 text-sm font-mono">
        <div><div className="text-[11px] uppercase tracking-wider text-zinc-500">Total Cartons</div><div className="text-base font-semibold">{totalCartons}</div></div>
        <div><div className="text-[11px] uppercase tracking-wider text-zinc-500">Total Gross</div><div className="text-base font-semibold">{grandGross.toFixed(3)} kg</div></div>
        <div><div className="text-[11px] uppercase tracking-wider text-zinc-500">Total Reported</div><div className="text-base font-semibold text-orange-700">{grandReported.toFixed(3)} kg</div></div>
      </div>
    </div>
  );
}

function EstimatedItemsTable({ order, productById }) {
  let totalCartons = 0, totalRolls = 0, totalNetKg = 0;
  let totalGranKg = 0, totalPibKg = 0, totalCores = 0, totalBoxes = 0;
  const rows = order.items.map((it, idx) => {
    const p = productById[it.product_id];
    const spec = { ...(p?.spec || {}), ...(it.spec || {}) };
    const rpc = spec.rolls_per_carton || 1;
    const cartons = it.quantity_rolls;
    const rolls = cartons * rpc;
    const perCartonNet = spec.roll_weight_kg || (spec.weight_per_roll_kg || 0) * rpc;
    const netKg = cartons * perCartonNet;
    const granulePct = (p?.granule_pct ?? 99.5) / 100;
    const granKg = netKg * granulePct;
    const pibKg = netKg * (1 - granulePct);
    const coreCount = rolls;          // one core per roll
    const boxCount = cartons;         // one carton box per carton
    totalCartons += cartons; totalRolls += rolls; totalNetKg += netKg;
    totalGranKg += granKg; totalPibKg += pibKg; totalCores += coreCount; totalBoxes += boxCount;
    return { idx, it, spec, rpc, cartons, rolls, perCartonNet, netKg, granKg, pibKg, coreCount, boxCount, p };
  });
  // Pretty description without the default "Transparent" color but with Length
  const describe = (r) => {
    const bits = [];
    if (r.spec.thickness_micron) bits.push(`${r.spec.thickness_micron}µ`);
    if (r.spec.width_mm) bits.push(`${r.spec.width_mm}mm`);
    if (r.spec.roll_length_m) bits.push(`Length ${r.spec.roll_length_m}m`);
    bits.push(`${(r.spec.weight_per_roll_kg || (r.spec.roll_weight_kg || 0) / r.rpc).toFixed(3)} kg/roll`);
    if (r.spec.color && r.spec.color.toLowerCase() !== "transparent") bits.push(r.spec.color);
    if (r.spec.grade) bits.push(r.spec.grade);
    return bits.join(" · ");
  };
  return (
    <div className="mt-6" data-testid="items-table">
      <div className="overflow-x-auto print:overflow-visible">
      <table className="w-full text-xs border-collapse">
        <thead className="bg-zinc-100 border-y-2 border-zinc-900">
          <tr className="font-mono uppercase tracking-wider text-[9px] text-zinc-700">
            <th className="text-left px-2 py-2 border border-zinc-300">#</th>
            <th className="text-left px-2 py-2 border border-zinc-300">Description</th>
            <th className="text-right px-2 py-2 border border-zinc-300 bg-orange-100 text-orange-800">Cartons</th>
            <th className="text-right px-2 py-2 border border-zinc-300">Rolls/Ctn</th>
            <th className="text-right px-2 py-2 border border-zinc-300">Rolls Inside</th>
            <th className="text-right px-2 py-2 border border-zinc-300">Est. Net (kg)</th>
            <th className="text-right px-2 py-2 border border-zinc-300 bg-orange-50">Granules (kg)</th>
            <th className="text-right px-2 py-2 border border-zinc-300 bg-orange-50">PIB (kg)</th>
            <th className="text-right px-2 py-2 border border-zinc-300 bg-orange-50">Core (pcs)</th>
            <th className="text-right px-2 py-2 border border-zinc-300 bg-orange-50">Carton (pcs)</th>
          </tr>
        </thead>
        <tbody className="font-mono">
          {rows.map((r) => (
            <tr key={r.idx} className="border-b border-zinc-200">
              <td className="px-2 py-2 border border-zinc-300 align-top">{r.idx + 1}</td>
              <td className="px-2 py-2 border border-zinc-300 align-top">
                <div className="font-semibold text-zinc-950">{r.p?.sku || "—"}</div>
                <div className="text-[10px] text-zinc-600">{describe(r)}</div>
              </td>
              <td className="px-2 py-2 border border-zinc-300 text-right align-top bg-orange-100 text-orange-800 font-bold">{r.cartons}</td>
              <td className="px-2 py-2 border border-zinc-300 text-right align-top">{r.rpc}</td>
              <td className="px-2 py-2 border border-zinc-300 text-right align-top">{r.rolls}</td>
              <td className="px-2 py-2 border border-zinc-300 text-right align-top">{r.netKg.toFixed(3)}</td>
              <td className="px-2 py-2 border border-zinc-300 text-right align-top bg-orange-50">{r.granKg.toFixed(3)}</td>
              <td className="px-2 py-2 border border-zinc-300 text-right align-top bg-orange-50">{r.pibKg.toFixed(3)}</td>
              <td className="px-2 py-2 border border-zinc-300 text-right align-top bg-orange-50">{r.coreCount}</td>
              <td className="px-2 py-2 border border-zinc-300 text-right align-top bg-orange-50">{r.boxCount}</td>
            </tr>
          ))}
        </tbody>
        <tfoot className="font-mono">
          <tr className="border-t-2 border-zinc-900 bg-zinc-50">
            <td className="px-2 py-2 border border-zinc-300 uppercase text-[10px] font-semibold" colSpan={2}>Total Raw Material Required</td>
            <td className="px-2 py-2 border border-zinc-300 text-right font-bold bg-orange-100 text-orange-800">{totalCartons} ctns</td>
            <td className="px-2 py-2 border border-zinc-300"></td>
            <td className="px-2 py-2 border border-zinc-300 text-right font-semibold">{totalRolls} rolls</td>
            <td className="px-2 py-2 border border-zinc-300 text-right font-semibold">{totalNetKg.toFixed(3)} kg</td>
            <td className="px-2 py-2 border border-zinc-300 text-right font-bold bg-orange-100 text-orange-800">{totalGranKg.toFixed(3)} kg</td>
            <td className="px-2 py-2 border border-zinc-300 text-right font-bold bg-orange-100 text-orange-800">{totalPibKg.toFixed(3)} kg</td>
            <td className="px-2 py-2 border border-zinc-300 text-right font-bold bg-orange-100 text-orange-800">{totalCores} pcs</td>
            <td className="px-2 py-2 border border-zinc-300 text-right font-bold bg-orange-100 text-orange-800">{totalBoxes} pcs</td>
          </tr>
        </tfoot>
      </table>
      </div>
    </div>
  );
}

function RawMaterialBreakdown({ order, productById, rawMaterials, assignedByPid }) {
  // Aggregate exact raw-material requirement keyed by inward item id.
  // If assignedByPid is provided (Production Note), subtract assigned cartons from each line so the
  // breakdown reflects ONLY the cartons that still need to be produced.
  const rmById = {};
  rawMaterials.forEach((rm) => { rmById[rm.id] = rm; });
  const agg = {};
  const addQty = (rmId, qty, fallbackLabel) => {
    if (qty <= 0) return;
    let rm = rmId ? rmById[rmId] : null;
    if (!rm && fallbackLabel) {
      // Synthetic placeholder when the product has no inward-item mapped for this material.
      // Group it under a stable synthetic id so multiple lines aggregate.
      const synId = `__synthetic__:${fallbackLabel.category}:${fallbackLabel.name}`;
      rm = rmById[synId];
      if (!rm) {
        rm = { id: synId, name: fallbackLabel.name + " (no item mapped)", specification: "", unit: fallbackLabel.unit, category: fallbackLabel.category };
        rmById[synId] = rm;
      }
    }
    if (!rm) return;
    const key = rm.id;
    if (!agg[key]) {
      agg[key] = { id: key, name: rm.name, spec: rm.specification || "", unit: rm.unit || "", category: rm.category, qty: 0 };
    }
    agg[key].qty += qty;
  };

  order.items.filter((it) => it.product_id).forEach((it) => {
    const p = productById[it.product_id];
    if (!p) return;
    const spec = { ...(p.spec || {}), ...(it.spec || {}) };
    const rpc = spec.rolls_per_carton || 1;
    const assigned = (assignedByPid?.[it.product_id]?.length) || 0;
    const cartons = Math.max(0, it.quantity_rolls - assigned); // remaining to be produced
    const rolls = cartons * rpc;
    const perCartonNet = spec.roll_weight_kg || (spec.weight_per_roll_kg || 0) * rpc;
    const netKg = cartons * perCartonNet;
    const granulePct = (p.granule_pct ?? 99.5) / 100;
    addQty(p.granule_item_id, netKg * granulePct, { name: "LLDPE Granules", unit: "kg", category: "granules" });
    addQty(p.additive_item_id, netKg * (1 - granulePct), { name: "PIB", unit: "kg", category: "master_batch" });
    addQty(p.core_item_id, rolls, { name: "Paper Core", unit: "nos", category: "paper_tube" });
    addQty(p.carton_item_id, cartons, { name: "Carton Box", unit: "nos", category: "carton" });
  });

  const groups = {
    granules: { label: "Granules", items: [] },
    master_batch: { label: "Additives (e.g. PIB)", items: [] },
    paper_tube: { label: "Cores", items: [] },
    carton: { label: "Cartons", items: [] },
    packaging: { label: "Packaging", items: [] },
    other: { label: "Other", items: [] },
  };
  Object.values(agg).forEach((row) => {
    (groups[row.category] || groups.other).items.push(row);
  });
  Object.values(groups).forEach((g) => g.items.sort((a, b) => a.name.localeCompare(b.name)));

  const visible = Object.entries(groups).filter(([, g]) => g.items.length > 0);
  const allInStock = !!assignedByPid && visible.length === 0;
  if (visible.length === 0 && !allInStock) return null;
  if (allInStock) {
    return (
      <div className="mt-4" data-testid="raw-material-breakdown">
        <div className="border border-dashed border-zinc-300 bg-zinc-50 px-4 py-4 text-xs font-mono text-zinc-700 text-center">
          ✓ All ordered cartons are already in finished stock. <span className="text-orange-700 font-semibold">No raw material to procure.</span>
        </div>
      </div>
    );
  }

  const fmtQty = (row) => {
    const unit = (row.unit || "").toLowerCase();
    if (unit === "kg") return `${row.qty.toFixed(3)} kg`;
    return `${Math.ceil(row.qty)} ${row.unit || "pcs"}`;
  };

  return (
    <div className="mt-4" data-testid="raw-material-breakdown">
      <div className="border border-orange-300">
        <div className="bg-orange-50 border-b border-orange-300 px-3 py-2 text-[10px] font-mono uppercase tracking-widest text-orange-800">
          Raw Material Required — itemised
        </div>
        <div className="overflow-x-auto print:overflow-visible">
        <table className="w-full text-xs border-collapse">
          <thead className="bg-zinc-50 text-zinc-500 uppercase text-[9px] font-mono tracking-wider">
            <tr>
              <th className="text-left px-2 py-1.5 border-b border-zinc-200 w-32">Category</th>
              <th className="text-left px-2 py-1.5 border-b border-zinc-200">Item</th>
              <th className="text-left px-2 py-1.5 border-b border-zinc-200">Specification</th>
              <th className="text-right px-2 py-1.5 border-b border-zinc-200">Quantity Required</th>
            </tr>
          </thead>
          <tbody className="font-mono">
            {visible.map(([cat, g]) => (
              g.items.map((row, idx) => (
                <tr key={row.id} className="border-b border-zinc-200">
                  {idx === 0 ? (
                    <td rowSpan={g.items.length} className="px-2 py-1.5 border-r border-zinc-200 align-top bg-zinc-50 text-[10px] uppercase tracking-wider text-zinc-700 font-semibold">{g.label}</td>
                  ) : null}
                  <td className="px-2 py-1.5">{row.name}</td>
                  <td className="px-2 py-1.5 text-zinc-600">{row.spec || "—"}</td>
                  <td className="px-2 py-1.5 text-right text-orange-700 font-semibold">{fmtQty(row)}</td>
                </tr>
              ))
            ))}
          </tbody>
        </table>
        </div>
        <div className="px-3 py-2 bg-zinc-50 border-t border-zinc-200 text-[10px] font-mono text-zinc-600">
          Quantities computed from spec defaults (granule % per SKU, rolls/carton, weight/roll). Round-up applied to piece counts.
        </div>
      </div>
    </div>
  );
}



function ClientNoteTable({ order, productById, compact }) {
  const labelOf = (cat) => cat === "gross" ? "Gross Weight" : cat === "net" ? "Net Weight" : "100% Net Weight";
  const items = order.items.filter((it) => it.product_id);
  const totalCartons = items.reduce((s, it) => s + (it.quantity_rolls || 0), 0);
  // +4pt mode (default) → text-lg / headers text-sm ; compact → text-sm / headers text-[11px]
  const bodySize = compact ? "text-sm" : "text-lg";
  const headSize = compact ? "text-[11px]" : "text-sm";
  const cell = compact ? "px-3 py-2" : "px-4 py-3";
  return (
    <div className="mt-6" data-testid="items-table">
      <div className="overflow-x-auto print:overflow-visible">
      <table className={`w-full ${bodySize} border-collapse`}>
        <thead className="bg-zinc-100 border-y-2 border-zinc-900">
          <tr className={`font-mono uppercase tracking-wider ${headSize} text-zinc-700`}>
            <th className={`text-left ${cell} border border-zinc-300 w-12`}>#</th>
            <th className={`text-left ${cell} border border-zinc-300`}>Item / Product</th>
            <th className={`text-left ${cell} border border-zinc-300`}>Weight Basis</th>
            <th className={`text-right ${cell} border border-zinc-300 bg-orange-100 text-orange-800`}>Carton Quantity</th>
          </tr>
        </thead>
        <tbody className="font-mono">
          {items.map((it, idx) => {
            const p = productById[it.product_id];
            return (
              <tr key={idx} className="border-b border-zinc-200">
                <td className={`${cell} border border-zinc-300 align-top`}>{idx + 1}</td>
                <td className={`${cell} border border-zinc-300 align-top font-semibold text-zinc-950`}>{p?.sku || "—"}</td>
                <td className={`${cell} border border-zinc-300 align-top text-zinc-700`}>{labelOf(it.weight_category || "pure_net")}</td>
                <td className={`${cell} border border-zinc-300 align-top text-right text-orange-700 font-bold bg-orange-50`}>{it.quantity_rolls} ctns</td>
              </tr>
            );
          })}
        </tbody>
        <tfoot className="font-mono">
          <tr className="border-t-2 border-zinc-900 bg-zinc-50">
            <td colSpan={3} className={`${cell} border border-zinc-300 uppercase ${headSize} font-semibold`}>Total</td>
            <td className={`${cell} border border-zinc-300 text-right font-bold text-orange-700 bg-orange-100`}>{totalCartons} ctns</td>
          </tr>
        </tfoot>
      </table>
      </div>
    </div>
  );
}

function ProductionItemsTable({ order, productById, assignedByPid, rawMaterials }) {
  const rmById = {};
  (rawMaterials || []).forEach((rm) => { rmById[rm.id] = rm; });
  let totalReq = 0, totalAssigned = 0, totalProd = 0, totalNetKg = 0;
  let totalGranKg = 0;
  const rows = order.items.filter((it) => it.product_id).map((it, idx) => {
    const p = productById[it.product_id];
    const spec = { ...(p?.spec || {}), ...(it.spec || {}) };
    const rpc = spec.rolls_per_carton || 1;
    const required = it.quantity_rolls;
    const assigned = (assignedByPid?.[it.product_id]?.length) || 0;
    const toProduce = Math.max(0, required - assigned);
    const perRollNet = spec.weight_per_roll_kg || ((spec.roll_weight_kg || 0) / rpc) || 0;
    const coreItem = p?.core_item_id ? rmById[p.core_item_id] : null;
    const coreUnitWt = coreItem?.unit_weight_kg || 0;
    const perRollWithCore = perRollNet + coreUnitWt;
    const netKg = toProduce * perRollNet * rpc;
    const granulePct = (p?.granule_pct ?? 99.5) / 100;
    const granKg = netKg * granulePct;
    totalReq += required; totalAssigned += assigned; totalProd += toProduce;
    totalNetKg += netKg; totalGranKg += granKg;
    return { idx, p, spec, rpc, required, assigned, toProduce, perRollNet, perRollWithCore, netKg, granKg };
  });
  const describe = (r) => {
    if (r.spec.width_mm) return `${r.spec.width_mm}mm`;
    return "—";
  };
  return (
    <div className="mt-6" data-testid="items-table">
      <div className="overflow-x-auto print:overflow-visible">
      <table className="w-full text-sm border-collapse">
        <thead className="bg-zinc-100 border-y-2 border-zinc-900">
          <tr className="font-mono uppercase tracking-wider text-[11px] text-zinc-700">
            <th className="text-left px-3 py-2.5 border border-zinc-300">#</th>
            <th className="text-left px-3 py-2.5 border border-zinc-300">Description</th>
            <th className="text-right px-3 py-2.5 border border-zinc-300">REQ</th>
            <th className="text-right px-3 py-2.5 border border-zinc-300">In Stock</th>
            <th className="text-right px-3 py-2.5 border border-zinc-300 bg-orange-100 text-orange-800">To Produce</th>
            <th className="text-right px-3 py-2.5 border border-zinc-300 leading-tight">Rolls /<br/>Ctn</th>
            <th className="text-right px-3 py-2.5 border border-zinc-300 leading-tight">Micron<br/>(µ)</th>
            <th className="text-right px-3 py-2.5 border border-zinc-300 leading-tight">Length /<br/>Roll (m)</th>
            <th className="text-right px-3 py-2.5 border border-zinc-300 leading-tight">Per Roll<br/>Net (kg)</th>
            <th className="text-right px-3 py-2.5 border border-zinc-300 leading-tight">Per Roll<br/>w/Core (kg)</th>
            <th className="text-right px-3 py-2.5 border border-zinc-300 leading-tight">Est. Net<br/>(kg)</th>
            <th className="text-right px-3 py-2.5 border border-zinc-300 bg-orange-50 leading-tight">Granules<br/>(kg)</th>
          </tr>
        </thead>
        <tbody className="font-mono">
          {rows.map((r) => (
            <tr key={r.idx} className="border-b border-zinc-200">
              <td className="px-3 py-2.5 border border-zinc-300 align-top">{r.idx + 1}</td>
              <td className="px-3 py-2.5 border border-zinc-300 align-top">
                <div className="font-semibold text-zinc-950">{r.p?.sku || "—"}</div>
                <div className="text-[11px] text-zinc-600">{describe(r)}</div>
              </td>
              <td className="px-3 py-2.5 border border-zinc-300 text-right align-top">{r.required}</td>
              <td className="px-3 py-2.5 border border-zinc-300 text-right align-top text-zinc-700">{r.assigned}</td>
              <td className="px-3 py-2.5 border border-zinc-300 text-right align-top bg-orange-100 text-orange-800 font-bold">{r.toProduce}</td>
              <td className="px-3 py-2.5 border border-zinc-300 text-right align-top">{r.rpc}</td>
              <td className="px-3 py-2.5 border border-zinc-300 text-right align-top">{r.spec.thickness_micron || "—"}</td>
              <td className="px-3 py-2.5 border border-zinc-300 text-right align-top">{r.spec.roll_length_m || "—"}</td>
              <td className="px-3 py-2.5 border border-zinc-300 text-right align-top">{r.perRollNet.toFixed(3)}</td>
              <td className="px-3 py-2.5 border border-zinc-300 text-right align-top text-zinc-700">{r.perRollWithCore.toFixed(3)}</td>
              <td className="px-3 py-2.5 border border-zinc-300 text-right align-top">{r.netKg.toFixed(3)}</td>
              <td className="px-3 py-2.5 border border-zinc-300 text-right align-top bg-orange-50">{r.granKg.toFixed(3)}</td>
            </tr>
          ))}
        </tbody>
        <tfoot className="font-mono">
          <tr className="border-t-2 border-zinc-900 bg-zinc-50">
            <td className="px-3 py-2.5 border border-zinc-300 uppercase text-[11px] font-semibold" colSpan={2}>Totals</td>
            <td className="px-3 py-2.5 border border-zinc-300 text-right font-semibold">{totalReq} ctns</td>
            <td className="px-3 py-2.5 border border-zinc-300 text-right font-semibold">{totalAssigned} ctns</td>
            <td className="px-3 py-2.5 border border-zinc-300 text-right font-bold bg-orange-100 text-orange-800">{totalProd} ctns</td>
            <td className="px-3 py-2.5 border border-zinc-300 text-right text-zinc-400">—</td>
            <td className="px-3 py-2.5 border border-zinc-300 text-right text-zinc-400">—</td>
            <td className="px-3 py-2.5 border border-zinc-300 text-right text-zinc-400">—</td>
            <td className="px-3 py-2.5 border border-zinc-300 text-right text-zinc-400">—</td>
            <td className="px-3 py-2.5 border border-zinc-300 text-right text-zinc-400">—</td>
            <td className="px-3 py-2.5 border border-zinc-300 text-right font-semibold">{totalNetKg.toFixed(3)} kg</td>
            <td className="px-3 py-2.5 border border-zinc-300 text-right font-bold bg-orange-100 text-orange-800">{totalGranKg.toFixed(3)} kg</td>
          </tr>
        </tfoot>
      </table>
      </div>
    </div>
  );
}
